import React from "react";
import { useAgency } from "./AgencyContext";

const G = "#0077B5";
const GRN = "#1D9E75";

function StatCard({ label, value, sub, color = "#1a1a1a" }) {
  return (
    <div style={{ background: "#fff", border: "0.5px solid rgba(0,0,0,0.1)", borderRadius: 12, padding: 20, textAlign: "center" }}>
      <div style={{ fontSize: 12, color: "#888", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 600, color }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

const STATUS_BADGE = {
  active: { bg: "#E1F5EE", color: "#0F6E56" },
  onboarding: { bg: "#E6F1FB", color: "#0C447C" },
  invited: { bg: "#f3f2ef", color: "#666" },
  paused: { bg: "#FAEEDA", color: "#633806" },
  churned: { bg: "#FAE4E4", color: "#8B1A1A" },
};

export default function AgencyDashboard() {
  const { agency, stats, clients, loading, error } = useAgency();

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>Loading…</div>;
  if (error === "not_agency") return <div style={{ padding: 40, textAlign: "center", color: "#888" }}>You don't have agency access.</div>;
  if (error) return <div style={{ padding: 40, textAlign: "center", color: "#D85A30" }}>{error}</div>;

  const rate = stats?.wholesale_rate || 15;
  const active = stats?.active_clients || 0;
  const cost = stats?.monthly_cost || (active * rate);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 500 }}>Dashboard</div>
        <div style={{ fontSize: 13, color: "#888" }}>{agency?.brand_name} — {active} active client{active !== 1 ? "s" : ""}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 28 }}>
        <StatCard label="Active clients" value={active} sub={`${stats?.onboarding_clients || 0} onboarding`} color={GRN} />
        <StatCard label="Total clients" value={stats?.total_clients || 0} />
        <StatCard label="Monthly cost" value={`$${cost}`} sub={`${active} x $${rate}`} />
        <StatCard label="Max clients" value={agency?.max_clients || 50} sub={`${(agency?.max_clients || 50) - (stats?.total_clients || 0)} remaining`} />
      </div>

      {/* Revenue summary */}
      <div style={{ background: "#fff", border: "0.5px solid rgba(0,0,0,0.1)", borderRadius: 12, padding: 20, marginBottom: 28 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Revenue summary</div>
        <div style={{ fontSize: 13, color: "#555", lineHeight: 1.8 }}>
          <div>{active} clients x ${rate} = <strong>${cost}</strong> to BrandHives</div>
          <div style={{ fontSize: 12, color: "#888" }}>Your margin = what you charge clients minus ${cost}/mo wholesale</div>
        </div>
      </div>

      {/* Client list (top 10) */}
      <div style={{ background: "#fff", border: "0.5px solid rgba(0,0,0,0.1)", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", borderBottom: "0.5px solid rgba(0,0,0,0.1)" }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Recent clients</div>
          <a href="#/agency/clients" style={{ fontSize: 13, color: G, textDecoration: "none" }}>View all →</a>
        </div>
        {clients.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", fontSize: 13, color: "#888" }}>
            No clients yet. <a href="#/agency/invite" style={{ color: G }}>Invite your first client →</a>
          </div>
        ) : (
          clients.map((c, i) => {
            const st = STATUS_BADGE[c.status] || STATUS_BADGE.invited;
            return (
              <div key={c.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", borderBottom: i < clients.length - 1 ? "0.5px solid rgba(0,0,0,0.05)" : "none" }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{c.client_name}</div>
                  <div style={{ fontSize: 11, color: "#888" }}>{c.client_email}</div>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 11, background: "#f3f2ef", color: "#666", padding: "2px 8px", borderRadius: 12 }}>{c.plan}</span>
                  <span style={{ fontSize: 11, background: st.bg, color: st.color, padding: "2px 8px", borderRadius: 12, fontWeight: 600 }}>{c.status}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div style={{ textAlign: "center" }}>
        <a href="#/agency/invite" style={{ display: "inline-block", background: G, color: "#fff", padding: "10px 24px", borderRadius: 8, fontSize: 14, fontWeight: 600, textDecoration: "none" }}>
          + Add Client
        </a>
      </div>
    </div>
  );
}
